/**
 * Logout Page Component
 * 
 * Displays a logout confirmation screen and provides navigation back to login.
 * Called after user session is terminated on the backend (OAuth/SSO logout).
 * 
 * Features:
 * - Branded logout message with product name
 * - Thank you message to user
 * - Redirect button to OAuth login endpoint
 * - Background image and styled card layout
 * - Error tracking for navigation issues
 * - Centralized error logging integration
 * 
 * @component
 * @returns {JSX.Element} Full-page logout UI with branding and redirect button
 * 
 * @requires react
 * @requires react-router-dom (useNavigate - currently unused but available)
 * @requires utils/error_tracker - Centralized error tracking
 * @requires utils/config_mapping - Product name configuration
 * @requires utils/config_ip_details - API server URL for OAuth endpoint
 * @requires assets/images/login_bg.svg - Background image asset
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import login_bg from "../../assets/images/login_bg.svg"; // Adjust the path as needed
import { trackError } from "../../utils/error_tracker"; // Import the centralized error tracking function
import {PRODUCT_NAME} from "../../utils/config_mapping";
import {API_SERVER_URL} from "../../utils/config_ip_details";

/**
 * Logout Component - Displays logout confirmation and redirect to login
 * 
 * Renders a branded logout page with:
 * - Product logo and branding
 * - Logout confirmation message
 * - Login redirect button linking to OAuth endpoint
 * - Background image for visual styling
 * 
 * @component
 * @returns {JSX.Element} Complete logout page UI
 */
export const Logout = () => {
    return (
        <div className="min-h-screen flex w-full bg-slate-50">
            <div className="min-h-screen w-screen flex items-center justify-center bg-background p-4">
                <div className="w-full max-w-md space-y-8">
                    <div className="text-center">
                        <div className="inline-flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-text w-6 h-6 text-white"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M10 9H8"></path><path d="M16 13H8"></path><path d="M16 17H8"></path></svg>
                            </div>
                            <h1 className="text-2xl font-bold text-foreground">ContractGenie<sup>TM</sup></h1>
                        </div>
                        <div className="space-y-2">
                            <h2 className="text-xl font-semibold text-foreground">You are Logged Out</h2>
                            <p className="text-muted-foreground">Thank you for using ContractGenie<sup>TM</sup>.</p>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card text-card-foreground shadow-lg">
                        <div className="flex flex-col p-6 space-y-1 text-center">
                            <h3 className="font-semibold tracking-tight text-lg">Session Ended</h3>
                            <p className="text-sm text-muted-foreground">You have been securely logged out.</p>
                        </div>
                        <div className="p-6 pt-0 space-y-6">
                            <a href={API_SERVER_URL+"login/"} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 rounded-md px-8 w-full h-12 bg-[#0078d4] hover:bg-[#106ebe] text-white font-medium shadow-sm">
                                <svg className="w-5 h-5 mr-3" viewBox="0 0 23 23" fill="currentColor"><path d="M11.03 0L0 1.95v8.85l11.03 6.2L22.06 10.8V1.95L11.03 0zm-5.39 14.8L1.95 13.53V3.87l3.69 1.04v9.89zm0-11.95L1.95 1.63 11.03 0l9.08 1.63-3.69 1.22-5.39-1.52L5.64 2.85zm10.78 11.95v-9.89l3.69-1.04v9.66l-3.69 1.27zm0-11.95l3.69-1.22v8.49l-3.69 1.04V2.85z"></path></svg>
                                Login Again
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
